import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import './BtcPrice.css';

const formatInput = (raw) => {
  const clean = String(raw).replace(/[^\d.]/g, '');
  const dot = clean.indexOf('.');
  const integer = (dot < 0 ? clean : clean.slice(0, dot))
    .replace(/^0+(?=\d)/, '');
  const decimals = dot < 0
    ? ''
    : clean.slice(dot + 1).replace(/\./g, '').slice(0, 2);

  return (
    integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
    (dot < 0 ? '' : `.${decimals}`)
  );
};

const toCents = (input) => {
  const plain = String(input).replace(/,/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(plain)) return null;

  const [whole, fraction = ''] = plain.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));

  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
};

const fromCents = (cents) =>
  `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;

const BtcPrice = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);

  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Restore the cursor after React renders the newly formatted value.
  useLayoutEffect(() => {
    if (pendingCaret.current === null || !inputRef.current) return;

    inputRef.current.setSelectionRange(
      pendingCaret.current,
      pendingCaret.current
    );
    pendingCaret.current = null;
  }, [price]);

  const handlePriceChange = (event) => {
    const raw = event.target.value;
    const cursor = event.target.selectionStart;
    const formatted = formatInput(raw);

    // Format only the part before the cursor to find its new position.
    pendingCaret.current = formatInput(raw.slice(0, cursor)).length;
    setPrice(formatted);

    // React might not render if the formatted value stays the same.
    if (formatted === price) {
      requestAnimationFrame(() => {
        if (inputRef.current && pendingCaret.current !== null) {
          inputRef.current.setSelectionRange(
            pendingCaret.current,
            pendingCaret.current
          );
          pendingCaret.current = null;
        }
      });
    }
  };

  const refresh = useCallback(async () => {
    const token = window.sessionStorage.getItem('usrtk');
    if (!token) return navigate('/login');

    setRefreshing(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/getbtc`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      const result = await response.json();

      if (!response.ok || result.type === 'error') {
        throw new Error(result.message || 'Could not get BTC price');
      }

      const current =
        typeof result === 'object'
          ? result.price ?? result.btcPrice ?? result.btc
          : result;
      const cents = toCents(current);

      if (cents === null) {
        throw new Error('The server returned an invalid BTC price');
      }

      pendingCaret.current = null;
      setPrice(formatInput(fromCents(cents)));
      setLoaded(true);
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message || 'Could not get BTC price'
      });
    } finally {
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = async (offset = 0) => {
    if (busy || refreshing) return;

    const cents = toCents(price);
    const updatedCents = cents === null ? null : cents + offset;

    if (
      updatedCents === null ||
      updatedCents <= 0 ||
      !Number.isSafeInteger(updatedCents)
    ) {
      Toast.fire({
        icon: 'error',
        title: 'Enter a valid price greater than zero'
      });
      return;
    }

    const next = fromCents(updatedCents);
    setBusy(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/updatebtc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${window.sessionStorage.getItem('usrtk')}`
          },
          body: JSON.stringify({ price: next })
        }
      );
      const result = await response.json();

      if (!response.ok || result.type === 'error') {
        throw new Error(result.message || 'Could not update BTC price');
      }

      pendingCaret.current = null;
      setPrice(formatInput(next));
      Toast.fire({
        icon: 'success',
        title: `BTC price updated to ${formatInput(next)}`
      });
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message || 'Could not update BTC price'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="btc-page">
      <section className="btc-card">
        <header className="btc-header">
          <div>
            <span className="btc-eyebrow">Manual pricing</span>
            <h1>BTC price</h1>
          </div>
          <button
            className="btc-refresh"
            type="button"
            onClick={refresh}
            disabled={busy || refreshing}
            aria-label="Refresh current BTC price"
            title="Refresh current price"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 1 0-2.3 6.7" />
              <path d="M20 4v7h-7" />
            </svg>
          </button>
        </header>

        <p className="btc-hint">
          {refreshing
            ? 'Getting current price…'
            : 'Enter the price in NGN, or adjust it by ₦0.01.'}
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label htmlFor="btc-price">Price (NGN)</label>
          <div className="btc-input-wrap">
            <span>₦</span>
            <input
              ref={inputRef}
              id="btc-price"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={price}
              onChange={handlePriceChange}
              disabled={busy || refreshing}
              aria-describedby="btc-format"
            />
          </div>
          <small id="btc-format">Example: 11,100,300.43</small>

          <button
            className="btc-primary"
            type="submit"
            disabled={busy || refreshing || !loaded}
          >
            {busy ? 'Updating…' : 'Update price'}
          </button>

          <div className="btc-adjustments">
            <button
              type="button"
              onClick={() => submit(1)}
              disabled={busy || refreshing || !loaded}
            >
              + ₦0.01 <span>and update</span>
            </button>
            <button
              type="button"
              onClick={() => submit(-1)}
              disabled={busy || refreshing || !loaded}
            >
              − ₦0.01 <span>and update</span>
            </button>
          </div>
        </form>

        <Link className="btc-back" to="/dashboard">
          ← Back to dashboard
        </Link>
      </section>
    </main>
  );
};

export default BtcPrice;